/**
 * Uygulama kaydı — yönetici kabulünde açılır (ödül sonrası değil).
 */

export const IMPLEMENTATION_METHODS = [
  { value: 'quick', label: 'Hızlı Aksiyon', hint: 'Basit çözüm, ≤30 gün' },
  { value: 'kobetsu', label: 'Kobetsu Projesi', hint: 'Analiz ve proje planı, 2–4 ay' },
  { value: 'project', label: 'Yatırım Projesi', hint: 'Bütçe veya teknik onay gerektirir' },
]

export const getImplementationMethodLabel = (value) =>
  IMPLEMENTATION_METHODS.find((m) => m.value === value)?.label || value || ''

export const BENEFIT_KINDS = [
  { value: 'measurable', label: 'Ölçülebilir' },
  { value: 'qualitative', label: 'Nitel' },
]

export const PLAN_PRIORITY_LABELS = {
  normal: 'Normal',
  high: 'Yüksek',
  critical: 'Kritik',
}

export const OPEX_VERIFIER_UNITS = [
  'OPEX',
  'Kalite + OPEX',
  'Finans + OPEX',
  'İSG + OPEX',
  'Bakım/Enerji + OPEX',
  'Enerji + Finans + OPEX',
  'İlgili Bölüm + OPEX',
]

export const OPEX_VERIFY_METHODS = [
  'Ölçüm / veri karşılaştırması',
  'Saha kontrolü + standart / kanıt',
  'Finansal hesap doğrulaması',
  'İSG risk değerlendirmesi',
  'Eğitim / katılım ve saha gözlemi',
]

export const OPEX_VERIFY_PERIODS = [
  'Uygulama sonrası hemen',
  'Uygulama sonrası 1 ay',
  'Uygulama sonrası 3 ay',
  'Uygulama sonrası 6 ay',
]

export const ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024
export const ATTACHMENT_MAX_FILES = 10
export const ATTACHMENT_ACCEPT =
  'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv'

const ATTACHMENT_EXT_RE =
  /\.(pdf|docx?|xlsx?|pptx?|txt|csv|jpe?g|png|gif|webp|heic|heif|bmp)$/i

export const validateAttachment = (file) => {
  if (!file) return 'Dosya seçilmedi.'
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return `${file.name}: dosya boyutu 15 MB'ı geçemez.`
  }
  const isImage = String(file.type || '').startsWith('image/')
  if (!isImage && !ATTACHMENT_EXT_RE.test(file.name || '')) {
    return `${file.name}: yalnızca fotoğraf, PDF, Word, Excel, PowerPoint, TXT veya CSV yüklenebilir.`
  }
  return ''
}

/** @deprecated alias */
export const validateActionAttachment = validateAttachment
export const ACTION_ATTACHMENT_MAX_BYTES = ATTACHMENT_MAX_BYTES
export const ACTION_ATTACHMENT_MAX_FILES = ATTACHMENT_MAX_FILES
export const ACTION_ATTACHMENT_ACCEPT = ATTACHMENT_ACCEPT

export const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}

/** RTDB array veya object → tıklanabilir ek listesi */
export const normalizeResultAttachments = (value) => {
  const list = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? Object.values(value)
      : []
  return list.filter((a) => a && (a.url || a.dataUrl) && a.name)
}

const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

export const formatDateDMY = (value, empty = '—') => {
  if (!value) return empty
  const m = ISO_DAY_RE.exec(String(value))
  if (m) return `${m[3]}/${m[2]}/${m[1]}`
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()}`
}

export const parseDMYToIso = (text) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(text || '').trim())
  if (!m) return ''
  const [, dd, mm, yyyy] = m
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
  if (
    d.getFullYear() !== Number(yyyy) ||
    d.getMonth() !== Number(mm) - 1 ||
    d.getDate() !== Number(dd)
  ) {
    return ''
  }
  return `${yyyy}-${mm}-${dd}`
}

/** Para birimi: süreç EURO (şablon ile uyumlu). Eski TL kayıtlar da € gösterilir. */
export const formatEur = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(n)} €`
}

/** @deprecated use formatEur */
export const formatTl = formatEur

/** Input gösterimi: 5000000 → 5.000.000 */
export const formatAmountInput = (value) => {
  if (value === null || value === undefined || value === '') return ''
  const digits = String(value).replace(/\D/g, '')
  if (!digits) return ''
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

/** Input parse: 5.000.000 → "5000000" */
export const parseAmountInput = (text) => String(text ?? '').replace(/\D/g, '')

export const amountInputToNumberOrNull = (value) => {
  const digits = parseAmountInput(value)
  if (!digits) return null
  const n = Number(digits)
  return Number.isFinite(n) ? n : null
}

/** OPEX doğrulamada seçilen gerçekleşen fayda alanı (= kurul grup anahtarı) */
export const ACTUAL_BENEFIT_AREAS = [
  'İş Sağlığı ve Güvenliği (İSG)',
  'Üretim Süreçlerinin İyileştirilmesi (Zaman Tasarrufu)',
  'Kalite Artışı & Hurda Azaltma & Hata Önleme',
  'Ekipman & Makine Verimliliği (OEE)',
  'İş Kolaylaştırma / Ergonomi',
  'Enerji Tasarrufu ve Verimlilik',
  'Maliyet Azaltma Önerileri',
  'Çevre Koruma & Sürdürülebilirlik',
  'İnovasyon ve Ürün Geliştirme',
  'Saha Düzeni & Görsellik (5S)',
  'Genel İyileştirme Önerileri',
]

export const ACHIEVEMENT_OPTIONS = [
  'Gerçekleşti',
  'Kısmen gerçekleşti',
  'Gerçekleşmedi',
]

export const VERIFICATION_CHOICE = {
  SAME: 'same',
  REVISE: 'revise',
}

export const VERIFICATION_CHOICE_LABELS = {
  [VERIFICATION_CHOICE.SAME]: 'Olduğu gibi doğrula',
  [VERIFICATION_CHOICE.REVISE]: 'Revize ederek doğrula',
}

export const formatUygulamaNumber = (year, sequence) =>
  `UY-${year}-${String(sequence).padStart(6, '0')}`

export const addDaysIso = (date, days) => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d.toISOString()
}

/**
 * Yönetici kabulünde oluşturulan uygulama iskeleti.
 */
export const buildImplementationFromAccept = ({
  implId,
  uygulamaNo,
  recordId,
  record,
  acceptPayload,
  createdAt,
}) => {
  const plan = acceptPayload?.plan || {}
  const benefit = acceptPayload?.benefit || {}
  return {
    id: implId,
    uygulamaNo,
    recordId,
    recordNumber: record?.recordNumber || '',
    facility: record?.applicant?.location || '',
    topic: record?.topic || '',
    currentState: record?.currentState || '',
    proposedState: record?.proposedState || '',
    applicantName: record?.applicant?.fullName || '',
    relatedDepartmentId: record?.relatedDepartmentId || '',
    relatedDepartmentName: record?.relatedDepartmentName || '',
    method: acceptPayload?.method || null,
    plan: {
      ownerName: plan.ownerName || record?.applicant?.fullName || '',
      leaderName: plan.leaderName || '',
      leaderEmail: String(plan.leaderEmail || '').trim().toLowerCase(),
      team: plan.team || '',
      startDate: plan.startDate || '',
      dueDate: plan.dueDate || '',
      priority: plan.priority || 'normal',
      workSummary: plan.workSummary || '',
      managerNote: plan.managerNote || '',
    },
    benefit: {
      kind: benefit.kind || 'measurable',
      type: benefit.type || '',
      baseline: benefit.baseline || '',
      target: benefit.target || '',
      unit: benefit.unit || '',
      kpi: benefit.kpi || '',
      measureSource: benefit.measureSource || '',
      estimatedCost: benefit.estimatedCost ?? null,
      estimatedGain: benefit.estimatedGain ?? null,
      qualitativeNote: benefit.qualitativeNote || '',
    },
    tracking: {
      appState: 'not_started',
      statusNote: '',
      completionDate: '',
      lastUpdateAt: createdAt,
      lastUpdateBy: '',
    },
    realization: null,
    opexVerification: null,
    boardDecision: null,
    attachments: {},
    createdAt,
    updatedAt: createdAt,
  }
}
